// Simplified version of the ERP's Components/Header.jsx — same AppBar
// pattern (customizable headerColor, fixed/static, profile dropdown,
// mobile drawer) but with this app's single nav destination instead of
// a server-driven menu tree, since the admin side only has one page
// group (supplier review) so far.
import React, { useRef, useState, useLayoutEffect } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Drawer,
  Divider,
  Menu,
  MenuItem,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import StoreIcon from "@mui/icons-material/Store";
import AssignmentIcon from "@mui/icons-material/Assignment";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import GavelIcon from "@mui/icons-material/Gavel";
import TableChartIcon from "@mui/icons-material/TableChart";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ListAltIcon from "@mui/icons-material/ListAlt";
import SummarizeIcon from "@mui/icons-material/Summarize";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import PaletteIcon from "@mui/icons-material/Palette";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import SettingsIcon from "@mui/icons-material/Settings";
import AssessmentIcon from "@mui/icons-material/Assessment";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import StorefrontIcon from "@mui/icons-material/Storefront";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ShoppingBasketIcon from "@mui/icons-material/ShoppingBasket";
import { useNavigate, useLocation } from "react-router-dom";
import { useThemeContext } from "../../contexts/ThemeContext";
import { getAdminAuthData, clearAdminAuthData, REVIEWER_ROLE_IDS, CWS_ROLE_IDS, CWS_ADMIN_ROLE_IDS, MF_ROLE_IDS, MF_ADMIN_ROLE_IDS, PRICE_VIEWER_ROLE_IDS, MISMATCH_REVIEWER_ROLE_IDS, CMS_DECLARATION_VIEWER_ROLE_IDS, QUALITY_CHECK_ROLE_IDS, RATE_FIXING_ROLE_IDS, RCS_ROLE_IDS, PRODUCT_LIST_MANAGER_ROLE_IDS, COMPANY_EDITOR_ROLE_IDS, REPORT_VIEWER_ROLE_IDS, PROCUREMENT_VIEWER_ROLE_IDS, TENDER_VIEWER_ROLE_IDS, MISMATCH_REPORT_VIEWER_ROLE_IDS, BRANDED_VENDOR_VIEWER_ROLE_IDS, MANUAL_SUPPLY_ORDER_VIEWER_ROLE_IDS, FMCG_VIEWER_ROLE_IDS, FMCG_STORE_ROLE_IDS, ROLE_NAMES } from "../../Utils/adminAuth";
import { getSupplierAuthData, clearSupplierAuthData } from "../../Utils/supplierAuth";
import AppearancePanel from "./AppearancePanel";
import NotificationBell from "./NotificationBell";
import TAMIL_LABELS, { BiLabel } from "../../Utils/tamil";

// Darkens a "#rrggbb" hex color by `amount` (0-1) — used to build the
// gradient partner color for the user's chosen headerColor (redesigned
// 2026-08-02 to match a client-provided reference: a gradient brand bar
// + a separate white single-row nav strip below it, rather than nav
// chips living directly on the brand color).
const darkenHex = (hex, amount) => {
  const num = parseInt(hex.replace("#", ""), 16);
  const clamp = (c) => Math.max(0, Math.min(255, c));
  const r = clamp((num >> 16) - Math.round(255 * amount));
  const g = clamp(((num >> 8) & 0x00ff) - Math.round(255 * amount));
  const b = clamp((num & 0x0000ff) - Math.round(255 * amount));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
};

// Shared header for BOTH identity spaces — internal staff (admin_*
// localStorage/JWT) and external suppliers (supplier_*). This is a
// standalone project now, not an ERP extraction that needs to visually
// match a separate Tailwind supplier app — one header/theme/layout
// system for the whole frontend. Whichever session is present in
// localStorage determines the branding/nav/logout behavior below; the
// two are mutually exclusive in practice (a browser is logged into one
// or the other, never both).
const AdminHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { headerColor, headerFixed } = useThemeContext();
  const { user } = getAdminAuthData();
  const { supplier } = getSupplierAuthData();
  const isSupplier = !user && Boolean(supplier);

  // Nav items scoped to the logged-in user's role — a CWS Admin never
  // sees the supplier-review link (and vice versa), though the backend
  // enforces this regardless of what the UI shows. Suppliers get a
  // small nav (Dashboard, Products, Tenders) now that product catalogue
  // management has its own page and tender bidding exists — previously
  // a single-page app with no nav at all.
  const NAV_ITEMS = isSupplier ? [
    { label: "Dashboard", path: "/dashboard", icon: <AssignmentIcon fontSize="small" /> },
    { label: "Products", path: "/products", icon: <Inventory2Icon fontSize="small" /> },
    { label: "Procurement", path: "/tenders", icon: <GavelIcon fontSize="small" /> },
    // Split out of an inline panel on the Procurement page into its own
    // nav entry (client ask, 2026-09-11: "have separate page for supplier
    // for additional supply request list").
    { label: "Additional Supply Requests", path: "/additional-supply-requests", icon: <NotificationsActiveIcon fontSize="small" /> },
    { label: "Deliveries", path: "/deliveries", icon: <LocalShippingIcon fontSize="small" /> },
    // Manual Supply Orders TNCCF sent to this supplier (2026-10-02).
    { label: "Supply Orders", path: "/supply-orders", icon: <ReceiptLongIcon fontSize="small" /> },
    // FMCG supply orders TNCCF sent to this supplier (2026-10-08).
    { label: "FMCG Supply Orders", path: "/fmcg-supply-orders", icon: <ReceiptLongIcon fontSize="small" /> },
    // CMS-only (they resell rather than produce, so TNCCF needs the
    // upstream purchase trail). Hidden for every other supplier type —
    // the backend enforces it regardless of what this shows.
    supplier?.supplier_type === "CMS" && { label: "Purchase Sources", path: "/source-declarations", icon: <FactCheckIcon fontSize="small" /> },
    // Importer / Authorized Supplier (2026-09-24, 2026-09-28) — Bill of Entry records for listed products.
    ["Importer", "Importer / Authorized Supplier"].includes(supplier?.supplier_type) && { label: "Import Records", path: "/import-records", icon: <DescriptionIcon fontSize="small" /> },
    { label: "Replacement Notices", path: "/replacement-notices", icon: <ReportProblemIcon fontSize="small" /> },
    { label: "Certificates", path: "/certificates", icon: <WorkspacePremiumIcon fontSize="small" /> },
  ].filter(Boolean) : [
    (REVIEWER_ROLE_IDS.includes(user?.role_id) || CWS_ROLE_IDS.includes(user?.role_id) || MF_ROLE_IDS.includes(user?.role_id)) && { label: "Dashboard", path: "/admin/dashboard", icon: <DashboardIcon fontSize="small" /> },
    // RCS's own dashboard, kept first in the list (same as every other
    // role's Dashboard entry) rather than down near its other RCS-only
    // items — it used to sit after several PROCUREMENT_VIEWER_ROLE_IDS
    // items (Indent Submitted, Product Demand, etc., which RCS also
    // matches), so an RCS user's Dashboard link landed mid-menu instead
    // of first (client-reported).
    RCS_ROLE_IDS.includes(user?.role_id) && { label: "Dashboard", path: "/admin/rcs-dashboard", icon: <DashboardIcon fontSize="small" /> },
    PROCUREMENT_VIEWER_ROLE_IDS.includes(user?.role_id) && { label: "Indent Submitted", path: "/admin/indent-review", icon: <FactCheckIcon fontSize="small" /> },
    PROCUREMENT_VIEWER_ROLE_IDS.includes(user?.role_id) && { label: "Product Demand", path: "/admin/indent-demand", icon: <SummarizeIcon fontSize="small" /> },
    PROCUREMENT_VIEWER_ROLE_IDS.includes(user?.role_id) && { label: "Registered Participants", path: "/admin/suppliers", icon: <PeopleIcon fontSize="small" /> },
    TENDER_VIEWER_ROLE_IDS.includes(user?.role_id) && { label: "Procurement", path: "/admin/tenders", icon: <GavelIcon fontSize="small" /> },
    PRICE_VIEWER_ROLE_IDS.includes(user?.role_id) && { label: "Price Review", path: "/admin/price-review", icon: <TableChartIcon fontSize="small" /> },
    // Supplementary (post-procurement) indent workflow (added 2026-09-08,
    // PLAN_supplementary_indent_workflow.md) — TNCCF Head's ask/hold
    // decisions on a store's late/additional requirement. Same gate as
    // Price Review (PRICE_VIEWER_ROLE_IDS + its own OTP step-up), since
    // this also touches an already-awarded supplier's rate/quantity.
    PRICE_VIEWER_ROLE_IDS.includes(user?.role_id) && { label: "Additional Supply Requests", path: "/admin/supplementary-requests", icon: <GavelIcon fontSize="small" /> },
    MISMATCH_REPORT_VIEWER_ROLE_IDS.includes(user?.role_id) && { label: "Mismatch Reports", path: "/admin/mismatch-reports", icon: <ReportProblemIcon fontSize="small" /> },
    MISMATCH_REVIEWER_ROLE_IDS.includes(user?.role_id) && { label: "Replacement Notices", path: "/admin/replacement-notices", icon: <GavelIcon fontSize="small" /> },
    // Read-only view of the Rate Fixing Committee's submissions (added
    // 2026-08-06, client ask: "need to show this details to TNCCF Head as
    // a separate menu, he can see the details added by the Rate Fixing
    // Committee") — own OTP step-up gate inside the page, same as Price
    // Review; Head can look, not record or edit.
    PRICE_VIEWER_ROLE_IDS.includes(user?.role_id) && { label: "Rate Fixing History", path: "/admin/rate-fixing-history", icon: <TrendingUpIcon fontSize="small" /> },
    // Read-only view of the Quality Check Committee's records (client
    // ask: "need to show this to the tnccf head") — same posture as Rate
    // Fixing History above, just no OTP step-up since there's no price
    // figure in this data. Head can look, not record.
    PRICE_VIEWER_ROLE_IDS.includes(user?.role_id) && { label: "Quality Check History", path: "/admin/quality-check-history", icon: <PhotoCameraIcon fontSize="small" /> },
    PROCUREMENT_VIEWER_ROLE_IDS.includes(user?.role_id) && { label: "Supply Monitoring", path: "/admin/supply-monitoring", icon: <LocalShippingOutlinedIcon fontSize="small" /> },
    // Branded Vendors + direct purchase orders (2026-09-27) — a separate
    // route to supply next to Procurement (tenders). View for Super Admin/
    // Admin/RCS; actions only for TNCCF Office/Head (enforced per page + API).
    BRANDED_VENDOR_VIEWER_ROLE_IDS.includes(user?.role_id) && { label: "Branded Vendors", path: "/admin/branded-vendors", icon: <StorefrontIcon fontSize="small" /> },
    BRANDED_VENDOR_VIEWER_ROLE_IDS.includes(user?.role_id) && { label: "Direct Purchase Orders", path: "/admin/direct-po", icon: <ReceiptLongIcon fontSize="small" /> },
    // Manual Supply Orders (2026-10-02) — store + supplier + products,
    // emailed as a Supply Order; independent of indents/tenders.
    MANUAL_SUPPLY_ORDER_VIEWER_ROLE_IDS.includes(user?.role_id) && { label: "Manual Supply Orders", path: "/admin/manual-supply-orders", icon: <AssignmentIcon fontSize="small" /> },
    // FMCG catalogue + FMCG indents (2026-10-07) — separate from JPC products/indents.
    FMCG_VIEWER_ROLE_IDS.includes(user?.role_id) && { label: "FMCG Summary", path: "/admin/fmcg-summary", icon: <ShoppingBasketIcon fontSize="small" /> },
    FMCG_VIEWER_ROLE_IDS.includes(user?.role_id) && { label: "FMCG Indents", path: "/admin/fmcg-indents", icon: <ShoppingBasketIcon fontSize="small" /> },
    FMCG_VIEWER_ROLE_IDS.includes(user?.role_id) && { label: "FMCG Supply Orders", path: "/admin/fmcg-supply-orders", icon: <ReceiptLongIcon fontSize="small" /> },
    FMCG_VIEWER_ROLE_IDS.includes(user?.role_id) && { label: "FMCG Products", path: "/admin/fmcg-products", icon: <Inventory2Icon fontSize="small" /> },
    FMCG_VIEWER_ROLE_IDS.includes(user?.role_id) && { label: "FMCG Masters", path: "/admin/fmcg-masters", icon: <Inventory2Icon fontSize="small" /> },
    FMCG_VIEWER_ROLE_IDS.includes(user?.role_id) && { label: "FMCG Supplier Products", path: "/admin/fmcg-supplier-products", icon: <Inventory2Icon fontSize="small" /> },
    // "Demand by Product" — re-added 2026-07-29 per client request
    // ("consolidated qty of all products"), now with a month filter +
    // print/export. Was hidden 2026-07-27; route/page/backend were
    // never removed, only unlinked from the nav.
    PROCUREMENT_VIEWER_ROLE_IDS.includes(user?.role_id) && { label: "Companies", path: "/admin/companies", icon: <PeopleIcon fontSize="small" /> },
    // Edit a store/godown's own address/detail record (added 2026-08-14,
    // PLAN_cws_store_multiple_delivery_areas.md) — same [5, 7] gate as
    // company editing, since this is the same TNCCF Office/Head editing
    // responsibility, just at the store level instead of the company level.
    COMPANY_EDITOR_ROLE_IDS.includes(user?.role_id) && { label: "Manage Stores", path: "/admin/stores", icon: <StoreIcon fontSize="small" /> },
    REVIEWER_ROLE_IDS.includes(user?.role_id) && { label: "Products", path: "/admin/products", icon: <Inventory2Icon fontSize="small" /> },
    QUALITY_CHECK_ROLE_IDS.includes(user?.role_id) && { label: "Quality Check", path: "/admin/quality-check", icon: <PhotoCameraIcon fontSize="small" /> },
    RATE_FIXING_ROLE_IDS.includes(user?.role_id) && { label: "Product Rate Fixation", path: "/admin/rate-fixing", icon: <TrendingUpIcon fontSize="small" /> },
    // Own nav item (added 2026-08-06, client ask: "have the source master
    // as a separate menu") — previously reachable only via a "Manage
    // Sources" link on the page above; now also a first-class nav entry,
    // same role gate.
    RATE_FIXING_ROLE_IDS.includes(user?.role_id) && { label: "Market Source Master", path: "/admin/rate-fixing/sources", icon: <SettingsIcon fontSize="small" /> },
    PRODUCT_LIST_MANAGER_ROLE_IDS.includes(user?.role_id) && { label: "Product Lists", path: "/admin/product-lists", icon: <ListAltIcon fontSize="small" /> },
    (CWS_ROLE_IDS.includes(user?.role_id) || MF_ROLE_IDS.includes(user?.role_id)) && { label: "Products", path: "/admin/procurement-products", icon: <Inventory2Icon fontSize="small" /> },
    (CWS_ROLE_IDS.includes(user?.role_id) || MF_ROLE_IDS.includes(user?.role_id)) && { label: "My Indents", path: "/admin/indents", icon: <AssignmentIcon fontSize="small" /> },
    // Manual Supply Orders TNCCF placed for this store (2026-10-02).
    (CWS_ROLE_IDS.includes(user?.role_id) || MF_ROLE_IDS.includes(user?.role_id)) && { label: "Supply Orders", path: "/admin/store-supply-orders", icon: <ReceiptLongIcon fontSize="small" /> },
    // The store's monthly FMCG indent (2026-10-07).
    FMCG_STORE_ROLE_IDS.includes(user?.role_id) && { label: "FMCG Indent", path: "/admin/fmcg-indents/my", icon: <ShoppingBasketIcon fontSize="small" /> },
    FMCG_STORE_ROLE_IDS.includes(user?.role_id) && { label: "FMCG Supply Orders", path: "/admin/fmcg-supply-orders/my", icon: <ReceiptLongIcon fontSize="small" /> },
    // Read-only listing of the company's own branches/godowns (added
    // 2026-08-13, client ask).
    // CWS_ROLE_IDS.includes(user?.role_id) && { label: "My Branches", path: "/admin/branches", icon: <StoreIcon fontSize="small" /> },
    // CWS Admin only (added 2026-08-13) — manage CWS User sub-accounts.
    // Approving/rejecting indents happens on My Indents/Indent Detail
    // instead of a separate page (client ask).
    CWS_ADMIN_ROLE_IDS.includes(user?.role_id) && { label: "Manage Users", path: "/admin/cws-users", icon: <PeopleIcon fontSize="small" /> },
    // Marketing Federation Admin only — same use case as the CWS entry above.
    MF_ADMIN_ROLE_IDS.includes(user?.role_id) && { label: "Manage Users", path: "/admin/mf-users", icon: <PeopleIcon fontSize="small" /> },
    (CWS_ROLE_IDS.includes(user?.role_id) || MF_ROLE_IDS.includes(user?.role_id)) && { label: "Quality and Quantity Management", path: "/admin/quality-quantity-management", icon: <LocalShippingOutlinedIcon fontSize="small" /> },
    // Moved to the end of the nav (client ask, 2026-08-05: "move the
    // pending procurement to the last") — was between "Registered
    // Participants" and "Price Review".
    // RCS (role_id 11, added 2026-08-07) — read-only Rate Fixing
    // Committee records, and the same read-only Products list CWS uses
    // (reuses /admin/procurement-products, not a separate page). Its
    // Dashboard entry lives at the top of this list now, see above.
    RCS_ROLE_IDS.includes(user?.role_id) && { label: "Rate Fixing Records", path: "/admin/rcs-rate-fixing", icon: <TrendingUpIcon fontSize="small" /> },
    RCS_ROLE_IDS.includes(user?.role_id) && { label: "Products", path: "/admin/procurement-products", icon: <Inventory2Icon fontSize="small" /> },
    // View-only history — RCS_ROLE_IDS specifically, not the wider
    // REPLACEMENT_NOTICE_VIEWER_ROLE_IDS: TNCCF Office/Head already have
    // "Replacement Notices" above (the full decision queue) and don't
    // need a second, duplicate nav entry for the same underlying data.
    RCS_ROLE_IDS.includes(user?.role_id) && { label: "Replacement Notices", path: "/admin/notices-history", icon: <ReportProblemIcon fontSize="small" /> },
    // Moved to the end of the nav (client ask, 2026-08-08).
    // Read-only oversight for TNCCF Head + RCS (added per client ask:
    // "this updates from the cms need to be shown to the tnccf head and
    // the rcs") — nothing here can edit a CMS supplier's own declaration.
    CMS_DECLARATION_VIEWER_ROLE_IDS.includes(user?.role_id) && { label: "CMS Purchase Sources", path: "/admin/cms-source-declarations", icon: <FactCheckIcon fontSize="small" /> },
    // Moved to the end of the nav (client ask, 2026-08-15).
    REPORT_VIEWER_ROLE_IDS.includes(user?.role_id) && { label: "Reports", path: "/admin/reports", icon: <AssessmentIcon fontSize="small" /> },
    // Moved to the end of the nav (client ask, 2026-09-01: "move the
    // minutes and document templates to the last in menus").
    PRODUCT_LIST_MANAGER_ROLE_IDS.includes(user?.role_id) && { label: "Minutes", path: "/admin/minutes/generate", icon: <PictureAsPdfIcon fontSize="small" /> },
    PRODUCT_LIST_MANAGER_ROLE_IDS.includes(user?.role_id) && { label: "Document Templates", path: "/admin/document-templates", icon: <DescriptionIcon fontSize="small" /> },
    // Moved to the end of the nav (client ask, 2026-09-17: "move the
    // settings to the last"). TNCCF Office/Head only — general settings
    // hub (currently: the login-page advertisement banner). Same [5, 7]
    // gate as Manage Stores/company editing above.
    COMPANY_EDITOR_ROLE_IDS.includes(user?.role_id) && { label: "Settings", path: "/admin/settings", icon: <SettingsIcon fontSize="small" /> },
  ].filter(Boolean);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileAnchor, setProfileAnchor] = useState(null);
  const [appearanceOpen, setAppearanceOpen] = useState(false);
  const [navHovered, setNavHovered] = useState(false);
  const [navScrollable, setNavScrollable] = useState(false);
  const navScrollRef = useRef(null);
  const scrollNav = (direction) => {
    const el = navScrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * 220, behavior: "smooth" });
  };

  // The nudge buttons only make sense when the nav strip is actually
  // overflowing — previously they showed on hover regardless, so they'd
  // pop up even when every item already fit on screen with nothing to
  // scroll to (client-reported). Re-checked on resize and whenever the
  // item count changes (role/login switch), since a strip that overflowed
  // at one width may not at another.
  useLayoutEffect(() => {
    const el = navScrollRef.current;
    if (!el) {
      setNavScrollable(false);
      return;
    }
    const checkOverflow = () => {
      setNavScrollable(el.scrollWidth - el.clientWidth > 1);
    };
    checkOverflow();
    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(el);
    window.addEventListener("resize", checkOverflow);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", checkOverflow);
    };
  }, [NAV_ITEMS.length, isMobile]);

  const isActive = (path) => location.pathname.startsWith(path);
  const homePath = isSupplier ? "/dashboard" : (NAV_ITEMS[0]?.path || "/admin/login");

  // Display fields normalized across both identity shapes — supplier
  // tokens carry supplier_name/mobile_no, admin tokens carry
  // full_name/email/role_id.
  const displayName = isSupplier ? supplier.supplier_name : user?.full_name;
  const displaySubtitle = isSupplier ? supplier.mobile_no : user?.email;
  const titleLine = isSupplier ? "Supplier" : (ROLE_NAMES[user?.role_id] || "Admin");
  // CWS Admin/User accounts are tied to one company (mst_jpc_user.cmp_id) —
  // most CWS Admin seed rows have full_name set to the company name itself,
  // but that's a seed-data convention, not something every CWS account can
  // rely on (a generically-named test account made this visible) — show
  // the real company name explicitly whenever the backend sends one.
  const displayCompany = !isSupplier && (CWS_ROLE_IDS.includes(user?.role_id) || MF_ROLE_IDS.includes(user?.role_id)) ? (user?.display_name || user?.company_name) : null;
  // Logo shown only for TNCCF (Office/Head), Quality Check Committee, and
  // Rate/Price Fixing Committee — every other role (Super Admin, Admin,
  // CWS, RCS, supplier, etc.) shows just the role name, no logo.
  const showLogo = !isSupplier && [5, 7, ...QUALITY_CHECK_ROLE_IDS, ...RATE_FIXING_ROLE_IDS].includes(user?.role_id);

  const handleLogout = () => {
    if (isSupplier) {
      clearSupplierAuthData();
      setProfileAnchor(null);
      navigate("/login");
      return;
    }
    clearAdminAuthData();
    setProfileAnchor(null);
    navigate("/admin/login");
  };

  const handleNav = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  return (
    <>
      {/* Redesigned 2026-08-02 to match a client-provided reference:
          a gradient brand bar (logo/title/subtitle + profile chip with
          name+chevron), with a SEPARATE white single-row nav strip
          underneath — nav items no longer live on the brand color at
          all. "sticky" (not "fixed") keeps the whole thing in normal
          document flow so the browser always reserves the right amount
          of space for it, at any height — no manual spacer needed. */}
      <AppBar
        position={headerFixed ? "sticky" : "static"}
        sx={{ top: 0, boxShadow: "0 2px 8px rgba(0,0,0,0.15)", overflow: "hidden" }}
      >
        <Box sx={{ background: `linear-gradient(120deg, ${headerColor} 0%, ${darkenHex(headerColor, 0.35)} 100%)`, transition: "background 0.3s ease" }}>
          <Toolbar sx={{ minHeight: { xs: 58, md: 72 }, px: { xs: 1.5, md: 3 }, gap: 1 }}>
            {isMobile && (
              <IconButton color="inherit" edge="start" onClick={() => setMobileOpen(true)}>
                <MenuIcon />
              </IconButton>
            )}

            <Box
              onClick={() => navigate(homePath)}
              sx={{ display: "flex", alignItems: "center", gap: 1.25, cursor: "pointer", minWidth: 0 }}
            >
              {showLogo && (
                <Box
                  component="img"
                  src="/images/tnccf_logo.png"
                  alt="TNCCF Logo"
                  sx={{
                    height: { xs: 34, md: 44 }, width: { xs: 34, md: 44 }, flexShrink: 0,
                    borderRadius: "50%", bgcolor: "#fff", p: 0.25, boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
                  }}
                />
              )}
              <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, fontSize: { xs: "0.95rem", md: "1.05rem" }, color: "#fff", lineHeight: 1.25 }} noWrap>
                  {titleLine}
                </Typography>
                {/* {!isMobile && (
                  <Typography sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.85)", lineHeight: 1.25 }} noWrap>
                    தமிழ்நாடு நுகர்வோர் பொருள் வாங்கும் கழகம்
                  </Typography>
                )} */}
              </Box>
            </Box>

            <Box sx={{ flexGrow: 1 }} />

            {/* Real in-app notifications (PLAN_web_notifications.md) —
                rendered once here, right before the profile chip, so it
                appears in both the desktop and mobile Toolbar (this
                block isn't itself branched on isMobile — only the nav
                strip/profile-name text below are). */}
            <NotificationBell />

            {/* Profile — avatar + name + dropdown chevron on desktop;
                just the avatar (same Menu) on mobile, to keep the top
                row single-line at any width. */}
            <Box
              onClick={(e) => setProfileAnchor(e.currentTarget)}
              sx={{
                display: "flex", alignItems: "center", gap: 1, cursor: "pointer",
                px: isMobile ? 0.25 : 1, py: 0.5, borderRadius: 2,
                "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
              }}
            >
              <Avatar sx={{ width: 34, height: 34, bgcolor: "primary.dark", fontSize: 14 }}>
                {(displayName || "A").charAt(0).toUpperCase()}
              </Avatar>
              {!isMobile && (
                <>
                  <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: "0.875rem" }} noWrap>
                    {displayName}
                  </Typography>
                  <KeyboardArrowDownIcon sx={{ color: "rgba(255,255,255,0.85)" }} fontSize="small" />
                </>
              )}
            </Box>

            <Menu
            anchorEl={profileAnchor}
            open={Boolean(profileAnchor)}
            onClose={() => setProfileAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{ sx: { borderRadius: 3, minWidth: 240, mt: 1, boxShadow: "0px 8px 24px rgba(0,0,0,0.12)" } }}
          >
            <Box sx={{ px: 2, py: 2 }}>
              <Typography variant="body2" fontWeight={600} color="text.primary">{displayName}</Typography>
              {displayCompany && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ wordBreak: "break-word" }}>
                  {displayCompany}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-all" }}>{displaySubtitle}</Typography>
            </Box>
            <Divider />
            <MenuItem onClick={() => { setProfileAnchor(null); setAppearanceOpen(true); }} sx={{ py: 1.2 }}>
              <ListItemIcon sx={{ minWidth: 32 }}><PaletteIcon fontSize="small" sx={{ color: headerColor }} /></ListItemIcon>
              <Typography variant="body2" fontWeight={500}><BiLabel label="Appearance" /></Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout} sx={{ py: 1.2 }}>
              <ListItemIcon sx={{ minWidth: 32 }}><LogoutIcon fontSize="small" color="error" /></ListItemIcon>
              <Typography variant="body2" fontWeight={500} color="error.main"><BiLabel label="Logout" /></Typography>
            </MenuItem>
          </Menu>
          </Toolbar>
        </Box>

        {/* White single-row nav strip — horizontally scrollable (never
            wraps to multiple lines) so it stays visually clean at any
            item count/screen width, matching the reference design. */}
        {!isMobile && (
          <Box
            onMouseEnter={() => setNavHovered(true)}
            onMouseLeave={() => setNavHovered(false)}
            sx={{ position: "relative", bgcolor: "#fff", borderTop: "1px solid rgba(0,0,0,0.06)" }}
          >
            {/* Left/right scroll nudge buttons — only rendered visible on
                hover (opacity, not conditional mount, so there's no layout
                shift/pop-in) since the strip's own horizontal scrollbar is
                hidden for a clean look; these give a discoverable way to
                reach items past the edge without a trackpad/shift-scroll. */}
            {navScrollable && (
              <>
                <IconButton
                  size="small"
                  onClick={() => scrollNav(-1)}
                  sx={{
                    position: "absolute", left: 2, top: "50%", transform: "translateY(-50%)",
                    zIndex: 2, bgcolor: "rgba(255,255,255,0.95)", boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
                    opacity: navHovered ? 1 : 0, pointerEvents: navHovered ? "auto" : "none",
                    transition: "opacity 0.15s",
                    "&:hover": { bgcolor: "#fff" },
                  }}
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => scrollNav(1)}
                  sx={{
                    position: "absolute", right: 2, top: "50%", transform: "translateY(-50%)",
                    zIndex: 2, bgcolor: "rgba(255,255,255,0.95)", boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
                    opacity: navHovered ? 1 : 0, pointerEvents: navHovered ? "auto" : "none",
                    transition: "opacity 0.15s",
                    "&:hover": { bgcolor: "#fff" },
                  }}
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </>
            )}

            <Box
              ref={navScrollRef}
              sx={{
                overflowX: "auto", overflowY: "hidden",
                "&::-webkit-scrollbar": { height: 0 }, scrollbarWidth: "none",
              }}
            >
              {/* mx: "auto" (not justifyContent:"center" on the scroll
                  container) — auto margins center this row when it's
                  narrower than the header (CWS Admin's 3 items, a
                  supplier's 5), but collapse to 0 the moment it's wider
                  than the header (TNCCF Head's 14 items) so it falls back
                  to plain left-aligned scrolling instead of starting the
                  scroll position mid-content with items cut off on both
                  sides — center-aligning an overflowing flex row would do
                  exactly that. */}
              <Box sx={{ display: "flex", width: "max-content", mx: "auto", px: 1 }}>
                {NAV_ITEMS.map((item) => {
                  const active = isActive(item.path);
                  return (
                    <Tooltip key={item.path} title={TAMIL_LABELS[item.label] || ""} arrow>
                      <Box
                        onClick={() => handleNav(item.path)}
                        sx={{
                          display: "flex", flexDirection: "column", alignItems: "center", gap: 0.4,
                          px: 2, py: 1.1, cursor: "pointer", flexShrink: 0,
                          color: active ? headerColor : "text.secondary",
                          bgcolor: active ? `${headerColor}14` : "transparent",
                          borderBottom: active ? `3px solid ${headerColor}` : "3px solid transparent",
                          "&:hover": { bgcolor: active ? `${headerColor}14` : "rgba(0,0,0,0.03)" },
                          transition: "background-color 0.15s",
                        }}
                      >
                        {item.icon}
                        <Typography variant="caption" sx={{ fontWeight: active ? 700 : 500, whiteSpace: "nowrap" }}>
                          {item.label}
                        </Typography>
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            </Box>
          </Box>
        )}
      </AppBar>

      <Dialog open={appearanceOpen} onClose={() => setAppearanceOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, m: 2 } }}>
        <DialogTitle sx={{ fontWeight: 600 }}><BiLabel label="Appearance" /></DialogTitle>
        <DialogContent sx={{ pb: 3 }}>
          <AppearancePanel />
        </DialogContent>
      </Dialog>

      {isMobile && (
        <Drawer anchor="left" open={mobileOpen} onClose={() => setMobileOpen(false)} PaperProps={{ sx: { width: 260 } }}>
          <Box sx={{ p: 2, bgcolor: headerColor, color: "#fff", display: "flex", alignItems: "center", gap: 1.25 }}>
            {showLogo && (
              <Box
                component="img"
                src="/images/tnccf_logo.png"
                alt="TNCCF Logo"
                sx={{ height: 32, width: 32, flexShrink: 0, borderRadius: "50%", bgcolor: "#fff", p: 0.25 }}
              />
            )}
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{titleLine}</Typography>
          </Box>
          <Divider />
          <List>
            {NAV_ITEMS.map((item) => (
              <ListItem key={item.path} disablePadding>
                <ListItemButton selected={isActive(item.path)} onClick={() => handleNav(item.path)}>
                  <ListItemIcon sx={{ minWidth: 40, color: headerColor }}>{item.icon}</ListItemIcon>
                  <ListItemText primary={<BiLabel label={item.label} />} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          <Divider />
          <List>
            <ListItem disablePadding>
              <ListItemButton onClick={() => { setMobileOpen(false); setAppearanceOpen(true); }}>
                <ListItemIcon sx={{ minWidth: 40, color: headerColor }}><PaletteIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary={<BiLabel label="Appearance" />} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={handleLogout}>
                <ListItemIcon sx={{ minWidth: 40, color: "error.main" }}><LogoutIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary={<BiLabel label="Logout" />} primaryTypographyProps={{ color: "error.main" }} />
              </ListItemButton>
            </ListItem>
          </List>
        </Drawer>
      )}
    </>
  );
};

export default AdminHeader;
